import React from 'react';
import {useIntl, FormattedMessage} from 'react-intl';
import {useHistory} from 'react-router-dom';
import Styled from 'styled-components/macro';
import {Breadcrumbs, Link, Modal} from '@forecast-it/design-system';
import {CSS_CONSTANTS} from '../../css_variables';
import {hasFeatureFlag} from '../shared/util/FeatureUtil';
import {FEATURE_FLAGS, SOCKET_ACTION} from '../../constants';
import Util from '../shared/util/util';
import {MODAL_TYPE, showModal} from '../shared/components/modals/generic_modal_conductor';
import {ConfirmationModalPrimaryButton} from '../shared/components/modals/general/ConfirmationModal';
import DeleteClientMutation from '../../mutations/delete_client_mutation';
import {createToast} from '../shared/components/toasts/another-toast/toaster';
import DropdownMenu from '../shared/components/dropdown-menu/DropdownMenu';
import {normalizeExternalUrl} from './normalizeExternalUrl';

type ClientStatus = {
	standing: 'ACTIVE' | 'INACTIVE';
};

type PrimaryContact = {
	id: string;
	firstName: string | null;
	lastName: string | null;
};

type Client = {
	id: string;
	name: string;
	website: string | null;
	city: string | null;
	country: string | null;
	region: string | null;
	statusId: string | null;
	createdAt: string | null;
	updatedAt: string | null;
};

type Props = {
	client: Client;
	status: ClientStatus | null;
	primaryContact: PrimaryContact | null;
	companyId: string;
	hasInvoices: boolean;
	// True when the client has at least one contact (any status). Deleting is blocked until zero.
	hasContacts?: boolean;
	// Whether the current user holds the permission that the backend actually validates for
	// editing this client. Computed once by the page and threaded down here rather than
	// re-derived, so there is a single place to change if the gate ever moves server-side. Gates
	// the kebab menu trigger (every item behind it needs this permission to do anything).
	canEditClient: boolean;
	// A SEPARATE permission, for the "All Clients" breadcrumb alone. Its destination is an
	// admin-only settings route, so this is not derivable from canEditClient — some profiles
	// hold one permission without the other.
	canReachClients: boolean;
};

// sky/sys/color/text/content/main
const COLOR_TEXT_MAIN = '#232334';
// sky/sys/color/text/content/auxiliary
const COLOR_TEXT_AUXILIARY = '#6b6d7b';
// sky/sys/color/brand/primary/1
const COLOR_BRAND_PRIMARY = '#0004db';
// sky/sys/color/id/9 (active/on-track green)
const COLOR_STATUS_ACTIVE = '#27bb8f';
// sky/sys/color/text/content/ondark
const COLOR_TEXT_ON_DARK = '#ffffff';

const Header = Styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

const PageHeader = Styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 8px 32px 16px 32px;
`;

const TitleRow = Styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const ClientName = Styled.h1`
	${props => props.theme.sys.font.heading['3']};
	color: ${COLOR_TEXT_MAIN};
	margin: 0;
`;

const TitleActions = Styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const StatusBadge = Styled.span<{standing: 'ACTIVE' | 'INACTIVE'}>`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	padding: 8px 16px;
	border: 2px solid ${({standing}) => (standing === 'ACTIVE' ? COLOR_STATUS_ACTIVE : CSS_CONSTANTS.v2_text_light_gray)};
	border-radius: 8px;
	/* body/1, not the tags token: tags is the one type token whose family is DM Mono
	   rather than the theme typeface, in both themes. */
	${props => props.theme.sys.font.body['1']};
	color: ${COLOR_TEXT_MAIN};
	background-color: #ffffff;
`;

const StatusDot = Styled.span<{standing: 'ACTIVE' | 'INACTIVE'}>`
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background-color: ${({standing}) => (standing === 'ACTIVE' ? COLOR_STATUS_ACTIVE : CSS_CONSTANTS.v2_text_light_gray)};
`;

const Infobar = Styled.div`
	display: flex;
	align-items: center;
	gap: 38px;
	padding-left: 32px;
	${props => props.theme.sys.font.body['1']};
`;

const DetailItem = Styled.span`
	display: flex;
	align-items: center;
	gap: 9px;
`;

const DetailLabel = Styled.span`
	color: ${COLOR_TEXT_AUXILIARY};
`;

const DetailValue = Styled.span`
	font-weight: 500;
	color: ${COLOR_TEXT_MAIN};
`;

const ContactAvatar = Styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
	border-radius: 4px;
	background-color: ${COLOR_BRAND_PRIMARY};
	${props => props.theme.comp.avatar.small.text};
	text-transform: uppercase;
	color: ${COLOR_TEXT_ON_DARK};
	flex-shrink: 0;
`;

const CompanyViewHeader: React.FC<Props> = ({
	client,
	status,
	primaryContact,
	companyId,
	hasInvoices,
	hasContacts,
	canEditClient,
	canReachClients,
}) => {
	const intl = useIntl();
	const history = useHistory();
	const primaryContactInitials = primaryContact
		? `${primaryContact.firstName?.[0] ?? ''}${primaryContact.lastName?.[0] ?? ''}`.toUpperCase()
		: null;
	const primaryContactName = primaryContact
		? [primaryContact.firstName, primaryContact.lastName].filter(Boolean).join(' ')
		: null;

	const locationParts = [client.city, client.region, client.country].filter(Boolean);
	const location = locationParts.length > 0 ? locationParts.join(', ') : null;

	const none = intl.formatMessage({id: 'company_view_page.field_none'});
	const clientsListPath = hasFeatureFlag(FEATURE_FLAGS.NEW_CLIENTS_FEATURE) ? '/clients' : '/admin/clients';
	const noEditTooltip = intl.formatMessage({id: 'company_view_page.no_edit_permission_tooltip'});

	const formatDate = (iso: string | null): string => {
		if (!iso) return none;
		return intl.formatDate(new Date(iso), {month: 'short', day: 'numeric', year: 'numeric'});
	};

	const handleDelete = () => {
		const onSuccess = () => {
			createToast({
				duration: 5000,
				message: intl.formatMessage({id: 'company_view_page.client_deleted_toast'}),
			});
			history.push(clientsListPath);
		};
		showModal({
			type: MODAL_TYPE.CONFIRMATION,
			title: intl.formatMessage({id: 'clients_page.delete_client_modal_title'}),
			description: (
				<div style={{whiteSpace: 'pre-line'}}>
					<FormattedMessage
						id="clients_page.delete_client_modal_description"
						values={{
							clientName: client.name,
							confirmText: SOCKET_ACTION.DELETE,
							b: (chunks: React.ReactNode) => <b>{chunks}</b>,
						}}
					/>
				</div>
			),
			modalSize: Modal.SIZE.MEDIUM,
			confirmPhrase: SOCKET_ACTION.DELETE,
			includeDefaultMessage: false,
			confirmationModalPrimaryButton: ConfirmationModalPrimaryButton.DELETE,
			callbackPositive: () => {
				Util.CommitMutation(DeleteClientMutation, {id: client.id, companyId}, onSuccess);
			},
		});
	};

	// A client can only be deleted once it has no invoices and no contacts. The invoiced tooltip
	// takes precedence when both conditions apply.
	const deleteDisabled = hasInvoices || hasContacts;
	const deleteTooltip = hasInvoices
		? intl.formatMessage({id: 'settings.client_delete_invoiced'})
		: hasContacts
		? intl.formatMessage({id: 'clients.client_delete_has_contacts'})
		: undefined;

	return (
		<Header>
			<PageHeader>
				{/*
				  Not rendered at all, rather than greyed out. A disabled breadcrumb advertises a
				  destination the user can never reach, and there is deliberately no replacement
				  affordance — browser Back and the Opportunity list are the route out.

				  The gate is canReachClients, not canEditClient: the /clients destination requires
				  a different, admin-only permission. Some profiles that can fully edit this page
				  therefore still lose the breadcrumb — correct, and a visible change worth calling
				  out to a reviewer.

				  The whole block goes, not just the link: a lone trailing crumb showing the client
				  name reads as a truncated trail, and the name is already the <h1> just below.
				*/}
				{canReachClients && (
					<Breadcrumbs>
						<Breadcrumbs.Item>
							<Link href={clientsListPath}>
								{intl.formatMessage({id: 'company_view_page.breadcrumb_all_clients'})}
							</Link>
						</Breadcrumbs.Item>
						<Breadcrumbs.Item>
							<span>{client.name}</span>
						</Breadcrumbs.Item>
					</Breadcrumbs>
				)}

				<TitleRow>
					<ClientName>{client.name}</ClientName>
					<TitleActions>
						{status && (
							<StatusBadge standing={status.standing}>
								<StatusDot standing={status.standing} />
								{intl.formatMessage({
									id:
										status.standing === 'ACTIVE'
											? 'company_view_page.status_active'
											: 'company_view_page.status_inactive',
								})}
							</StatusBadge>
						)}
						{/*
						  Each item carries its own gate, and the menu is only disabled when none of
						  them would render. Gating the whole menu on canEditClient instead would
						  cut the wrong way in both directions, because DropdownMenu's disabled
						  branch renders no children at all: an admin without CLIENT_UPDATE lost
						  Integration Links — navigation, not an edit action, and a destination they
						  can reach — while a Coordinator holding CLIENT_UPDATE but not
						  MANAGE_ACCOUNT_SETTINGS was shown an Integration Links item pointing at an
						  admin route they would bounce off.
						*/}
						<DropdownMenu disabled={!canEditClient && !canReachClients} disabledTooltip={noEditTooltip}>
							{canEditClient && (
								<DropdownMenu.Item
									onClick={() => showModal({type: MODAL_TYPE.EDIT_CLIENT_DETAILS, clientId: client.id})}
								>
									{intl.formatMessage({id: 'company_view_page.edit_client'})}
								</DropdownMenu.Item>
							)}
							{/* Navigation into /admin/clients/view-client/:id, so it follows the same
							    admin-only gate as the breadcrumb, not the edit gate. */}
							{canReachClients && (
								<DropdownMenu.Item
									onClick={() => history.push(`/admin/clients/view-client/${client.id}`)}
									tooltip={intl.formatMessage({id: 'company_view_page.integration_links_tooltip'})}
								>
									{intl.formatMessage({id: 'company_view_page.integration_links'})}
								</DropdownMenu.Item>
							)}
							{canEditClient && (
								<DropdownMenu.Item
									onClick={handleDelete}
									disabled={deleteDisabled}
									tooltip={deleteTooltip}
									dataCy="delete-client"
								>
									{intl.formatMessage({id: 'company_view_page.delete_client'})}
								</DropdownMenu.Item>
							)}
						</DropdownMenu>
					</TitleActions>
				</TitleRow>
			</PageHeader>

			<Infobar>
				<DetailItem>
					<DetailLabel>{intl.formatMessage({id: 'company_view_page.website_label'})}</DetailLabel>
					<DetailValue>
						{client.website ? (
							<a
								href={normalizeExternalUrl(client.website)}
								target="_blank"
								rel="noopener noreferrer"
								data-cy="client-website-link"
							>
								{client.website}
							</a>
						) : (
							none
						)}
					</DetailValue>
				</DetailItem>
				<DetailItem>
					<DetailLabel>{intl.formatMessage({id: 'company_view_page.primary_contact_label'})}</DetailLabel>
					{primaryContact ? (
						<>
							<ContactAvatar>{primaryContactInitials}</ContactAvatar>
							<DetailValue>{primaryContactName}</DetailValue>
						</>
					) : (
						<DetailValue>{none}</DetailValue>
					)}
				</DetailItem>
				<DetailItem>
					<DetailLabel>{intl.formatMessage({id: 'company_view_page.client_location_label'})}</DetailLabel>
					<DetailValue>{location ?? none}</DetailValue>
				</DetailItem>
				<DetailItem>
					<DetailLabel>{intl.formatMessage({id: 'company_view_page.created_date_label'})}</DetailLabel>
					<DetailValue>{formatDate(client.createdAt)}</DetailValue>
				</DetailItem>
				<DetailItem>
					<DetailLabel>{intl.formatMessage({id: 'company_view_page.modified_date_label'})}</DetailLabel>
					<DetailValue>{formatDate(client.updatedAt)}</DetailValue>
				</DetailItem>
			</Infobar>
		</Header>
	);
};

export default CompanyViewHeader;
